import {
  compose,
  path,
  toString,
  split,
  converge,
  append,
  last,
  init,
  chain,
  defaultTo,
  pipe,
  Path,
} from 'ramda';
import { INodeData } from '../Components/QueryComponents/NodeQuery/NodeQuery';
import { IFormData } from '../Features/Delsystem/_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';

export const getStringValue = (
  data: INodeData | null,
  aPath: string[]
): string => {
  if (data === null || data === undefined) {
    return '';
  }
  const value = path(aPath, data);
  if (typeof value === 'string') {
    return value;
  }
  if (value === null) {
    return '';
  }
  return toString(value);
};

export const getValue = (
  data: INodeData | null,
  aPath: string[]
): string | number | boolean | null => {
  if (data === null || data === undefined) {
    return null;
  }
  const value = pipe(path(aPath), defaultTo(null))(data);

  if (
    value !== null &&
    typeof value !== 'boolean' &&
    typeof value !== 'string' &&
    typeof value !== 'number'
  ) {
    throw new Error(
      `Could not understand value of type: ${typeof value} on attribute ${aPath.join(
        '.'
      )}`
    );
  }
  return value;
};

export const getStringValueFromDotPath = (
  data: INodeData | null,
  dotDelimitedPath: string
): string => getStringValue(data, attributeDefToDataPath(dotDelimitedPath));

export const getValueFromDotPath = (
  data: INodeData | null,
  dotDelimitedPath: string
): string | number | boolean | null =>
  getValue(data, attributeDefToDataPath(dotDelimitedPath));

/**
 * Creates a path array from a path string, if string contains no dots, an array with the string as its only element will be returned
 * - If attributeDef contains at least one dot, prepend '_Associaitons'
 * - If attributeDef contains two or more dots also intersperse '_Associations' in the inital part of the path
 *
 * Ex1: attributeDefToDataPath("ass1.ass2.attr") => ["_Associations", "ass1", "_Associations", "ass2", "attr"]
 * EX2: attributeDefToDataPath("attr") => ["attr"]
 *
 * @param attributeDef A path to an attribute (Ex: "associationName1.attributeName")
 */
export const attributeDefToDataPath: (attributeDef: string) => string[] =
  compose(
    converge(append, [
      last,
      (defParts: string[]) =>
        chain(
          (associationName: string) => ['_Associations', associationName],
          init(defParts)
        ),
    ]),
    split('.')
  );

/**
 * Creates a path array from a path string, if string contains no dots, an array with the string as its only element will be returned
 * - If attributeDef contains at least one dot, prepend '_Associations' and add "_Values" to the associationName
 * - If attributeDef contains two or more dots also intersperse '_Associations' in the inital part of the path
 *
 * Ex1: attributeDefToDataPath("ass1.ass2.attr") => ["_Associations", "ass1_Values", "_Associations", "ass2_Values", "attr"]
 * EX2: attributeDefToDataPath("attr") => ["attr"]
 *
 * @param attributeDef A path to an attribute (Ex: "associationName1.attributeName")
 */
export const attributeDefToValuePath = (
  attributeDef: string,
  operation: string = 'Values'
): string[] => {
  return compose(
    converge(append, [
      last,
      (defParts: string[]) =>
        chain(
          (associationName: string) => [
            '_Associations',
            `${associationName}_${operation}`,
          ],
          init(defParts)
        ),
    ]),
    split('.')
  )(attributeDef);
};
export const getVersion = (
  data: INodeData | null,
  associationPath: string
): number | undefined => {
  if (data == null) {
    return undefined;
  }

  const versionPath =
    associationPath.length > 0 ? associationPath + '._version' : '_version';

  const versionPathArray = attributeDefToDataPath(versionPath);

  const version = path<number>(versionPathArray, data);
  return version;
};

export const getId = (
  data: INodeData | null,
  associationPath: string
): string | undefined => {
  if (data == null) {
    return undefined;
  }

  const idPath = associationPath.length > 0 ? associationPath + '.id' : 'id';

  const attributePathArray = attributeDefToDataPath(idPath);

  const id = path<string>(attributePathArray, data);
  return id;
};

export const getVersionAndId = (
  data: IFormData | null,
  dataPath: Path,
  updateId: string | undefined
): IFormData => {
  const versionAndId = {
    id: path([...dataPath, 'id'], data) ?? updateId,
    _version: path([...dataPath, '_version'], data),
  };

  return versionAndId;
};
